type TranslationOverride = 'control' | 'enable_vietnamese' | 'enable_all';

// The values here are currently duplicated in:
//   projects/apps/yammer-teams-branded-experiences/config/tasks/appBundler.ts
//   projects/yammer-teams/config/tasks/appBundler.ts
//   projects/yammer-teams-meeting/config/tasks/appBundler.ts
// Until these all get moved to some central location, ensure changes here get added to these also!
const languageTagToSupportedLocaleMap: Record<string, string> = {
  ar: 'ar-sa',
  ca: 'ca-es',
  cs: 'cs-cz',
  da: 'da-dk',
  de: 'de-de',
  el: 'el-gr',
  en: 'en-us',
  es: 'es-es',
  fi: 'fi-fi',
  fr: 'fr-fr',
  he: 'he-il',
  hu: 'hu-hu',
  id: 'id-id',
  it: 'it-it',
  ja: 'ja-jp',
  ko: 'ko-kr',
  nb: 'nb-no',
  nn: 'nb-no',
  no: 'nb-no',
  nl: 'nl-nl',
  pl: 'pl-pl',
  pt: 'pt-pt',
  ro: 'ro-ro',
  ru: 'ru-ru',
  sv: 'sv-se',
  th: 'th-th',
  tr: 'tr-tr',
  uk: 'uk-ua',
};

const languageTagToSupportedLocaleMapWithVietnamese: Record<string, string> = {
  ...languageTagToSupportedLocaleMap,
  vi: 'vi-vn',
};

const languageTagToSupportedLocaleMapWithAllLanguages: Record<string, string> = {
  ...languageTagToSupportedLocaleMapWithVietnamese,
  sk: 'sk-sk',
  hr: 'hr-hr',
  sl: 'sl-si',
  sr: 'sr-latn-rs',
  lt: 'lt-lt',
  et: 'et-ee',
  bg: 'bg-bg',
  lv: 'lv-lv',
};

// These values map Yammer locales to the supported cultures here:
// https://docs.microsoft.com/en-us/bingmaps/rest-services/common-parameters-and-types/supported-culture-codes
const languageTagToSupportedCultureMap: Record<string, string> = {
  ar: 'ar',
  ca: 'ca',
  cs: 'cs',
  da: 'da',
  de: 'de',
  el: 'el',
  en: 'en-us',
  es: 'es',
  fi: 'fi',
  fr: 'fr',
  he: 'he',
  hu: 'hu',
  id: 'id',
  it: 'it',
  ja: 'ja',
  ko: 'ko',
  nb: 'no',
  nl: 'nl',
  pl: 'pl',
  pt: 'pt',
  ro: 'ro',
  ru: 'ru',
  sv: 'sv',
  th: 'th',
  tr: 'tr',
  uk: 'uk',
};

const languageTagToSupportedCultureMapWithVietnamese: Record<string, string> = {
  ...languageTagToSupportedCultureMap,
  vi: 'vi',
};

const languageTagToSupportedCultureMapWithAllLanguages: Record<string, string> = {
  ...languageTagToSupportedCultureMapWithVietnamese,
  sk: 'sk',
  hr: 'hr',
  sr: 'sr-latn',
  sl: 'sl',
  lt: 'lt',
  et: 'et',
  bg: 'bg',
  lv: 'lv',
};

// These are CELA approved fallbacks.
// More info: https://microsoft.sharepoint.com/sites/globalreadiness/SitePages/Language-fallback.aspx
//
// Note: zh-hk -> zh-tw is only permissible since we're using zh-tw as a neutral Traditional Chinese,
// and don't use any Taiwan-specific items, such as Taiwan Calendar.
// More details in thread:
// https://web.yammer.com/main/threads/eyJfdHlwZSI6IlRocmVhZCIsImlkIjoiNzM5MTUyMjEyMjIxOTUyIn0
//
// The values here are currently duplicated in:
//   projects/yammer-teams/config/tasks/appBundler.ts
//   projects/yammer-teams-meeting/config/tasks/appBundler.ts
// Until these all get moved to some central location, ensure changes here get added to these also!
const localeToSupportedLocaleOverrideMap: Record<string, string> = {
  'el-cy': 'en-us',
  'pt-mo': 'en-us',
  'pt-br': 'pt-br',
  'tr-cy': 'en-us',
  'zh-cn': 'zh-cn',
  'zh-mo': 'zh-cn',
  'zh-sg': 'zh-cn',
  'zh-tw': 'zh-tw',
  'zh-hk': 'zh-tw',
};

const localeToSupportedLocaleOverrideMapWithAllLanguages: Record<string, string> = {
  ...localeToSupportedLocaleOverrideMap,
  'en-gb': 'en-gb',
  'es-mx': 'es-mx',
  'fr-ca': 'fr-ca',
};

export const defaultLocale = 'en-us';

const isPseudoLocale = (localeInLowerCase: string) => localeInLowerCase.startsWith('qps-');

const getSupportedLocaleOverrideMap = (translationOverride?: TranslationOverride) => {
  if (translationOverride === 'enable_all') {
    return localeToSupportedLocaleOverrideMapWithAllLanguages;
  }

  return localeToSupportedLocaleOverrideMap;
};

const getLanguageTagToSupportedLocaleMap = (translationOverride?: TranslationOverride) => {
  if (translationOverride === 'enable_all') {
    return languageTagToSupportedLocaleMapWithAllLanguages;
  }

  if (translationOverride === 'enable_vietnamese') {
    return languageTagToSupportedLocaleMapWithVietnamese;
  }

  return languageTagToSupportedLocaleMap;
};

const getLanguageTagToSupportedCultureMap = (translationOverride?: TranslationOverride) => {
  if (translationOverride === 'enable_all') {
    return languageTagToSupportedCultureMapWithAllLanguages;
  }

  if (translationOverride === 'enable_vietnamese') {
    return languageTagToSupportedCultureMapWithVietnamese;
  }

  return languageTagToSupportedCultureMap;
};

const getSupportedLocaleFromLanguageTag = (localeInLowerCase: string, translationOverride?: TranslationOverride) => {
  const languageTag = localeInLowerCase.split('-')[0];

  const languageTagToSupportedLocaleMap = getLanguageTagToSupportedLocaleMap(translationOverride);
  const supportedLocale = languageTagToSupportedLocaleMap[languageTag];

  return supportedLocale ? supportedLocale : defaultLocale;
};

type GetSupportedCultureFromLocale = (locale: string, translationOverride?: TranslationOverride) => string;
export const getSupportedCultureFromLocale: GetSupportedCultureFromLocale = (
  locale: string,
  translationOverride?: TranslationOverride
) => {
  const languageTag = locale.toLowerCase().split('-')[0];
  const languageTagToSupportedCultureMap = getLanguageTagToSupportedCultureMap(translationOverride);
  const supportedCulture = languageTagToSupportedCultureMap[languageTag];

  return supportedCulture || defaultLocale;
};

export type GetSupportedLocale = (locale: string, translationOverride?: TranslationOverride) => string;
export const getSupportedLocale: GetSupportedLocale = (locale, translationOverride) => {
  if (!locale) {
    return defaultLocale;
  }

  const localeInLowerCase = locale.toLowerCase();
  if (isPseudoLocale(localeInLowerCase)) {
    return localeInLowerCase;
  }

  const supportedLocaleOverrideMap = getSupportedLocaleOverrideMap(translationOverride);

  const supportedLocale = supportedLocaleOverrideMap[localeInLowerCase];

  return supportedLocale ? supportedLocale : getSupportedLocaleFromLanguageTag(localeInLowerCase, translationOverride);
};
